/**
 * Pure constructors and per-kind defaults for the floor-plan model. All pure:
 * every function returns a fresh value and never touches its inputs, the
 * same contract as the rest of the edit catalog.
 */
import { freeElementTypeOf } from '../freeElementTypes';
import {
  CORNER_NAMES,
  type CornerName,
  type CustomAreaType,
  type EdgeSegmentType,
  type FloorPlanCorner,
  type FloorPlanEdgeSegment,
  type FloorPlanFreeElement,
  type FloorPlanPrimaryEdge,
  type FloorPlanRoom,
  SEGMENT_FIELD_DEFAULTS,
} from '../schemas';

/** An edge with no segments. */
export function noEdge(): FloorPlanPrimaryEdge {
  return { segments: [] };
}

/** Create a corner with no chamfer. */
export function makeCorner(dx = 0, dy = 0): FloorPlanCorner {
  return { dx, dy };
}

/**
 * Creates a fresh alcove EdgeSegment with zero chamfers and empty sub-edges.
 */
export function makeAlcove(
  position: number,
  width: number,
  depth: number,
): FloorPlanEdgeSegment {
  return {
    edgeSegmentType: 'alcove',
    position,
    width,
    depth,
    backLeftCorner: makeCorner(),
    backRightCorner: makeCorner(),
    leftEdge: noEdge(),
    backEdge: noEdge(),
    rightEdge: noEdge(),
  };
}

const squareCorners = () => Object.fromEntries(CORNER_NAMES.map(name => [name, makeCorner()])) as Record<CornerName, FloorPlanCorner>;

/** Every optional structure of a room, present and empty: four edges with no segments, four square corners, no free elements. */
export const emptyRoomStructure = () => ({
  backEdge: noEdge(),
  rightEdge: noEdge(),
  frontEdge: noEdge(),
  leftEdge: noEdge(),
  ...squareCorners(),
  freeElements: [] as FloorPlanFreeElement[],
});

/**
 * Wall thickness (4 inches), the ONE value every representation shares: the
 * 3D walls, the floor and ceiling slabs that extend out under them, exports,
 * and the 2D plan's wall band. Inner perimeters are what the room measures;
 * this is how far the structure reaches beyond them.
 */
export const WALL_THICKNESS = 4 / 12;

/** Smallest dimension (ft) a free element (flooring / counter / wall / object)
 *  may be dragged or typed down to. 1 inch: lets walls be thin partitions. */
export const MIN_FREE_ELEMENT_SIZE = 1 / 12;

/** Standard US interior door height (6'8"). */
export const DOOR_HEIGHT = 6 + 8 / 12;

/** Standard US interior door width (2'8"). */
export const DOOR_WIDTH = 2 + 8 / 12;

/** A fresh free element (interior rectangle) with square corners, made with its type's own fields (`freeElementTypes`). */
export function makeFreeElement(
  kind: CustomAreaType,
  x: number,
  y: number,
  width: number,
  length: number,
  floorType?: string,
): FloorPlanFreeElement {
  return { customAreaType: kind, ...freeElementTypeOf(kind).madeWith, x, y, width, length, floorType, ...squareCorners() } as FloorPlanFreeElement;
}

/**
 * A copy of the segment as the given kind, with that kind's defaults filled
 * in: doors get a swing + door-height opening, windows a 4-ft opening on a
 * 3-ft sill, openings a floor-level base. Existing values win unless `force`.
 * Pure: the input segment is untouched.
 */
export function edgeItemWithDefaults(
  segment: FloorPlanEdgeSegment,
  kind: EdgeSegmentType,
  force = false,
): FloorPlanEdgeSegment {
  const next: FloorPlanEdgeSegment = { ...segment, edgeSegmentType: kind };
  if (kind === 'door') {
    if (force || !next.swing) next.swing = 'inLeft';
    if (force || next.openingHeight == null) next.openingHeight = DOOR_HEIGHT;
  } else if (kind === 'window') {
    if (force || next.openingHeight == null) next.openingHeight = 4;
    if (force || next.openingBase == null) next.openingBase = 3;
  } else if (kind === 'opening') {
    if (force || next.openingBase == null) next.openingBase = SEGMENT_FIELD_DEFAULTS.openingBase;
  }
  return next;
}

/** The editor's opening state: a 10×10 room, 9-ft walls, no alcoves. */
export function defaultShape(): FloorPlanRoom {
  return { width: 10, length: 10, height: 9, floorType: 'Default', ...emptyRoomStructure() };
}
